// UltraFlex Admin Portal - Asset Management
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Package, Plus, Search, Filter, Edit, Trash2, Wrench,
  ChevronDown, X, Save, AlertCircle, CheckCircle,
  Building2, Calendar, DollarSign, Tag, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAssets = () => {
  const { token } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [options, setOptions] = useState({ types: [], subtypes: [], conditions: [], statuses: [] });
  
  // Filters
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  
  // Summary
  const [siteSummaries, setSiteSummaries] = useState([]);
  const [maintenanceDue, setMaintenanceDue] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [selectedSite, selectedType, selectedStatus, searchQuery]);

  const fetchInitialData = async () => {
    try {
      // Fetch dropdown options
      const optionsRes = await fetch(`${API_URL}/api/assets/options`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const optionsData = await optionsRes.json();
      setOptions(optionsData);

      // Fetch sites
      const sitesRes = await fetch(`${API_URL}/api/sites`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const sitesData = await sitesRes.json();
      setSites(sitesData.items || []);

      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/assets/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setSiteSummaries(statsData.by_site || []);
      }

      // Fetch maintenance due
      const maintenanceRes = await fetch(`${API_URL}/api/assets/maintenance/due?days_ahead=14`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (maintenanceRes.ok) {
        const maintenanceData = await maintenanceRes.json();
        setMaintenanceDue(maintenanceData.items || []);
      }
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    }
  };

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedSite) params.append('site_id', selectedSite);
      if (selectedType) params.append('asset_type', selectedType);
      if (selectedStatus) params.append('status', selectedStatus);
      if (searchQuery) params.append('search', searchQuery);

      const res = await fetch(`${API_URL}/api/assets?${params}`);
      const data = await res.json();
      setAssets(data.items || []);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = async (assetData) => {
    try {
      const res = await fetch(`${API_URL}/api/assets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(assetData)
      });
      if (res.ok) {
        fetchAssets();
        fetchInitialData();
        setShowAddModal(false);
      } else {
        throw new Error('Failed to create asset');
      }
    } catch (error) {
      alert('Failed to create asset');
    }
  };

  const handleUpdateAsset = async (assetId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/assets/${assetId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        fetchAssets();
        setShowEditModal(false);
        setSelectedAsset(null);
      } else {
        throw new Error('Failed to update asset');
      }
    } catch (error) {
      alert('Failed to update asset');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to retire this asset?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/assets/${assetId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchAssets();
        fetchInitialData();
      } else {
        throw new Error('Failed to delete asset');
      }
    } catch (error) {
      alert('Failed to delete asset');
    }
  };

  const handleAddMaintenance = async (assetId, maintenanceData) => {
    try {
      const res = await fetch(`${API_URL}/api/assets/${assetId}/maintenance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(maintenanceData)
      });
      if (res.ok) {
        fetchAssets();
        fetchInitialData();
        setShowMaintenanceModal(false);
        setSelectedAsset(null);
      } else {
        throw new Error('Failed to add maintenance record');
      }
    } catch (error) {
      alert('Failed to add maintenance record');
    }
  };

  const getConditionColor = (condition) => {
    const colors = {
      excellent: 'green',
      good: 'blue',
      fair: 'yellow',
      poor: 'orange',
      out_of_service: 'red'
    };
    return colors[condition] || 'gray';
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'green',
      maintenance: 'yellow',
      repair: 'orange',
      retired: 'gray',
      on_order: 'blue',
      in_storage: 'purple'
    };
    return colors[status] || 'gray';
  };

  return (
    <div className="min-h-screen bg-uf-black" data-testid="admin-assets-page">
      {/* Header */}
      <div className="bg-uf-surface border-b border-uf-border px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-uf-text">Asset Management</h1>
            <p className="text-uf-text-muted mt-1">Track and manage gym equipment across all sites</p>
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="flex items-center px-4 py-2 bg-uf-red text-white rounded-lg hover:bg-uf-red-hover"
            data-testid="add-asset-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Asset
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="px-8 py-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Assets" 
          value={siteSummaries.reduce((sum, s) => sum + (s.total_assets || 0), 0)}
          icon={Package}
          color="blue"
        />
        <StatCard 
          title="Active Sites" 
          value={siteSummaries.length}
          icon={Building2}
          color="green"
        />
        <StatCard 
          title="Maintenance Due" 
          value={maintenanceDue.length}
          icon={Wrench}
          color="orange"
        />
        <StatCard 
          title="Out of Service" 
          value={siteSummaries.reduce((sum, s) => sum + (s.by_condition?.out_of_service || 0), 0)}
          icon={AlertCircle}
          color="red"
        />
      </div>

      {/* Filters */}
      <div className="px-8 py-4 bg-uf-surface-hi border-y border-uf-border flex flex-wrap items-center gap-4">
        <Filter className="w-5 h-5 text-uf-text-muted" />
        
        <select
          value={selectedSite}
          onChange={(e) => setSelectedSite(e.target.value)}
          className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
        >
          <option value="">All Sites</option>
          {sites.map(site => (
            <option key={site.id} value={site.id}>{site.name}</option>
          ))}
        </select>

        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
        >
          <option value="">All Types</option>
          {(options.types || []).map(type => {
            const val = typeof type === 'string' ? type : (type.value || type);
            const label = typeof type === 'string' ? type.replace(/_/g, ' ') : (type.label || type.value || '');
            return <option key={val} value={val}>{label}</option>;
          })}
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
        >
          <option value="">All Statuses</option>
          {(options.statuses || []).map(status => (
            <option key={status.value || status} value={status.value || status}>{status.label || (typeof status === 'string' ? status.replace(/_/g, ' ') : '')}</option>
          ))}
        </select>

        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-uf-text-muted" />
          <input
            type="text"
            placeholder="Search assets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-uf-surface border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
          />
        </div>
      </div>

      {/* Assets Grid */}
      <div className="p-8">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto"></div>
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 mx-auto mb-4 text-uf-text-muted opacity-50" />
            <p className="text-uf-text-muted">No assets found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map(asset => (
              <AssetCard
                key={asset.id}
                asset={asset}
                sites={sites}
                onEdit={() => { setSelectedAsset(asset); setShowEditModal(true); }}
                onDelete={() => handleDeleteAsset(asset.id)}
                onMaintenance={() => { setSelectedAsset(asset); setShowMaintenanceModal(true); }}
                getConditionColor={getConditionColor}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Asset Modal */}
      {showAddModal && (
        <AssetFormModal
          options={options}
          sites={sites}
          onClose={() => setShowAddModal(false)}
          onSave={handleCreateAsset}
          isNew
        />
      )}

      {/* Edit Asset Modal */}
      {showEditModal && selectedAsset && (
        <AssetFormModal
          asset={selectedAsset}
          options={options}
          sites={sites}
          onClose={() => { setShowEditModal(false); setSelectedAsset(null); }}
          onSave={(updates) => handleUpdateAsset(selectedAsset.id, updates)}
        />
      )}

      {/* Maintenance Modal */}
      {showMaintenanceModal && selectedAsset && (
        <MaintenanceModal
          asset={selectedAsset}
          options={options}
          onClose={() => { setShowMaintenanceModal(false); setSelectedAsset(null); }}
          onSave={(data) => handleAddMaintenance(selectedAsset.id, data)}
        />
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-uf-surface rounded-lg p-5 border border-uf-border">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-uf-text-muted text-sm">{title}</p>
        <p className="text-2xl font-bold text-uf-text mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-lg bg-${color}-500/10`}>
        <Icon className={`w-6 h-6 text-${color}-500`} />
      </div>
    </div>
  </div>
);

// Asset Card Component
const AssetCard = ({ asset, sites, onEdit, onDelete, onMaintenance, getConditionColor, getStatusColor }) => {
  const site = sites.find(s => s.id === asset.site_id);
  const conditionColor = getConditionColor(asset.condition);
  const statusColor = getStatusColor(asset.status);

  return (
    <div className="bg-uf-surface rounded-lg border border-uf-border overflow-hidden hover:border-uf-red/50 transition-colors" data-testid={`asset-card-${asset.id}`}>
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <span className="font-mono text-xs text-uf-red bg-uf-red/10 px-2 py-0.5 rounded">
              {asset.asset_tag}
            </span>
            <h3 className="text-lg font-semibold text-uf-text mt-1">{asset.name}</h3>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-medium bg-${statusColor}-500/10 text-${statusColor}-400`}>
            {asset.status?.replace('_', ' ')}
          </span>
        </div>

        {/* Details */}
        <div className="space-y-2 text-sm text-uf-text-muted mb-4">
          <div className="flex items-center">
            <Tag className="w-4 h-4 mr-2 text-uf-red" />
            {asset.asset_type?.replace('_', ' ')} / {asset.asset_subtype?.replace('_', ' ')}
          </div>
          <div className="flex items-center">
            <Building2 className="w-4 h-4 mr-2 text-uf-red" />
            {site?.name || asset.site_id}
          </div>
          {asset.brand && (
            <div className="flex items-center">
              <Info className="w-4 h-4 mr-2 text-uf-red" />
              {asset.brand} {asset.model || ''}
            </div>
          )}
        </div>

        {/* Condition Badge */}
        <div className="flex items-center justify-between py-3 border-t border-uf-border">
          <span className="text-sm text-uf-text-muted">Condition</span>
          <span className={`px-2 py-1 rounded text-xs font-medium bg-${conditionColor}-500/10 text-${conditionColor}-400`}>
            {asset.condition?.replace('_', ' ')}
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t border-uf-border">
          <button 
            onClick={onMaintenance}
            className="flex-1 flex items-center justify-center px-3 py-2 bg-uf-surface-hi border border-uf-border rounded text-uf-text text-sm hover:bg-orange-500/20 hover:border-orange-500/50 hover:text-orange-400 transition-colors"
          >
            <Wrench className="w-4 h-4 mr-1" /> Maintenance
          </button>
          <button 
            onClick={onEdit}
            className="flex items-center justify-center p-2 bg-uf-surface-hi border border-uf-border rounded text-uf-text hover:bg-uf-red hover:text-white hover:border-uf-red transition-colors"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button 
            onClick={onDelete}
            className="flex items-center justify-center p-2 bg-uf-surface-hi border border-uf-border rounded text-uf-text hover:bg-red-500/20 hover:text-red-400 hover:border-red-500/50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Asset Form Modal
const AssetFormModal = ({ asset, options, sites, onClose, onSave, isNew = false }) => {
  const [formData, setFormData] = useState({
    name: asset?.name || '',
    site_id: asset?.site_id || '',
    asset_type: asset?.asset_type || '',
    asset_subtype: asset?.asset_subtype || '',
    brand: asset?.brand || '',
    model: asset?.model || '',
    serial_number: asset?.serial_number || '',
    purchase_date: asset?.purchase_date || '',
    purchase_price: asset?.purchase_price || '',
    warranty_expiry: asset?.warranty_expiry || '',
    condition: asset?.condition || 'good',
    status: asset?.status || 'active',
    location_in_gym: asset?.location_in_gym || '',
    notes: asset?.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const availableSubtypes = (options.subtypes && formData.asset_type && typeof options.subtypes === 'object' && !Array.isArray(options.subtypes))
    ? (options.subtypes[formData.asset_type] || [])
    : Array.isArray(options.subtypes) ? options.subtypes : [];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-uf-surface rounded-lg border border-uf-border w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-uf-border flex items-center justify-between">
          <h2 className="text-xl font-bold text-uf-text">
            {isNew ? 'Add New Asset' : `Edit Asset: ${asset?.asset_tag}`}
          </h2>
          <button onClick={onClose} className="text-uf-text-muted hover:text-uf-text">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[70vh] space-y-4">
          {/* Basic Info */}
          <div>
            <label className="block text-sm text-uf-text-muted mb-1">Asset Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Life Fitness Treadmill #1"
              required
              className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Site *</label>
              <select
                value={formData.site_id}
                onChange={(e) => setFormData({...formData, site_id: e.target.value})}
                required
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              >
                <option value="">Select Site</option>
                {sites.map(site => (
                  <option key={site.id} value={site.id}>{site.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Location in Gym</label>
              <input
                type="text"
                value={formData.location_in_gym}
                onChange={(e) => setFormData({...formData, location_in_gym: e.target.value})}
                placeholder="e.g., Cardio Floor, Free Weights Zone"
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Type *</label>
              <select
                value={formData.asset_type}
                onChange={(e) => setFormData({...formData, asset_type: e.target.value, asset_subtype: ''})}
                required
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              >
                <option value="">Select Type</option>
                {(options.types || []).map(type => {
                  const val = typeof type === 'string' ? type : type.value;
                  const label = typeof type === 'string' ? type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : type.label;
                  return <option key={val} value={val}>{label}</option>;
                })}
              </select>
            </div>
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Subtype *</label>
              <select
                value={formData.asset_subtype}
                onChange={(e) => setFormData({...formData, asset_subtype: e.target.value})}
                required
                disabled={!formData.asset_type}
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none disabled:opacity-50"
              >
                <option value="">Select Subtype</option>
                {(availableSubtypes || []).map(subtype => (
                  <option key={subtype.value || subtype} value={subtype.value || subtype}>{subtype.label || (typeof subtype === 'string' ? subtype.replace(/_/g, ' ') : '')}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Brand</label>
              <input
                type="text"
                value={formData.brand}
                onChange={(e) => setFormData({...formData, brand: e.target.value})}
                placeholder="e.g., Life Fitness, Hammer Strength"
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Model</label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({...formData, model: e.target.value})}
                placeholder="e.g., 95T Elevation"
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-uf-text-muted mb-1">Serial Number</label>
            <input
              type="text"
              value={formData.serial_number}
              onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
              className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Purchase Date</label>
              <input
                type="date"
                value={formData.purchase_date}
                onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Purchase Price (£)</label>
              <input
                type="number"
                value={formData.purchase_price}
                onChange={(e) => setFormData({...formData, purchase_price: parseFloat(e.target.value) || ''})}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Warranty Expiry</label>
              <input
                type="date"
                value={formData.warranty_expiry}
                onChange={(e) => setFormData({...formData, warranty_expiry: e.target.value})}
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Condition</label>
              <select
                value={formData.condition}
                onChange={(e) => setFormData({...formData, condition: e.target.value})}
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              >
                {(options.conditions || []).map(cond => (
                  <option key={cond.value || cond} value={cond.value || cond}>{cond.label || (typeof cond === 'string' ? cond.replace(/_/g, ' ') : '')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              >
                {(options.statuses || []).map(status => {
                  const val = typeof status === 'string' ? status : (status.value || status);
                  const label = typeof status === 'string' ? status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : (status.label || status.value || '');
                  return <option key={val} value={val}>{label}</option>;
                })}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-uf-text-muted mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={3}
              className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
            />
          </div>
        </form>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-uf-border flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-uf-border text-uf-text rounded-lg hover:bg-uf-surface-hi"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-uf-red text-white rounded-lg hover:bg-uf-red-hover disabled:opacity-50 flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : isNew ? 'Create Asset' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Maintenance Modal
const MaintenanceModal = ({ asset, options, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    maintenance_type: 'routine',
    description: '',
    performed_by: '',
    cost: '',
    parts_replaced: '',
    next_maintenance_due: '',
    condition_before: asset?.condition || 'good',
    condition_after: 'good',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        cost: formData.cost ? parseFloat(formData.cost) : null,
        parts_replaced: formData.parts_replaced ? formData.parts_replaced.split(',').map(p => p.trim()) : null
      };
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-uf-surface rounded-lg border border-uf-border w-full max-w-lg max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-uf-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-uf-text">Log Maintenance</h2>
            <p className="text-uf-text-muted text-sm">{asset?.asset_tag} - {asset?.name}</p>
          </div>
          <button onClick={onClose} className="text-uf-text-muted hover:text-uf-text">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Maintenance Type *</label>
              <select
                value={formData.maintenance_type}
                onChange={(e) => setFormData({...formData, maintenance_type: e.target.value})}
                required
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              >
                <option value="routine">Routine</option>
                <option value="repair">Repair</option>
                <option value="inspection">Inspection</option>
                <option value="replacement">Part Replacement</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Performed By *</label>
              <input
                type="text"
                value={formData.performed_by}
                onChange={(e) => setFormData({...formData, performed_by: e.target.value})}
                placeholder="Staff name or contractor"
                required
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-uf-text-muted mb-1">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              required
              rows={3}
              placeholder="What maintenance was performed?"
              className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Condition Before</label>
              <select
                value={formData.condition_before}
                onChange={(e) => setFormData({...formData, condition_before: e.target.value})}
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              >
                {(options.conditions || []).map(cond => (
                  <option key={cond.value || cond} value={cond.value || cond}>{cond.label || (typeof cond === 'string' ? cond.replace(/_/g, ' ') : '')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Condition After</label>
              <select
                value={formData.condition_after}
                onChange={(e) => setFormData({...formData, condition_after: e.target.value})}
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              >
                {(options.conditions || []).map(cond => (
                  <option key={cond.value || cond} value={cond.value || cond}>{cond.label || (typeof cond === 'string' ? cond.replace(/_/g, ' ') : '')}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Cost (£)</label>
              <input
                type="number"
                value={formData.cost}
                onChange={(e) => setFormData({...formData, cost: e.target.value})}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-uf-text-muted mb-1">Next Maintenance Due</label>
              <input
                type="date"
                value={formData.next_maintenance_due}
                onChange={(e) => setFormData({...formData, next_maintenance_due: e.target.value})}
                className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-uf-text-muted mb-1">Parts Replaced (comma separated)</label>
            <input
              type="text"
              value={formData.parts_replaced}
              onChange={(e) => setFormData({...formData, parts_replaced: e.target.value})}
              placeholder="e.g., Belt, Motor, Display"
              className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-uf-text-muted mb-1">Additional Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={2}
              className="w-full px-4 py-2 bg-uf-black border border-uf-border rounded-lg text-uf-text focus:ring-2 focus:ring-uf-red focus:outline-none"
            />
          </div>
        </form>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-uf-border flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-uf-border text-uf-text rounded-lg hover:bg-uf-surface-hi"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center"
          >
            <Wrench className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Log Maintenance'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAssets;
